/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */

/*
 * IL CALENDARIO
 * =============
 *
 * Sullo stile di Google Calendar: giorno, settimana, mese, anno.
 *
 * Mostra due cose diverse e le disegna in modo diverso:
 *   - APPUNTAMENTI (quando si e' detto al cliente che si passa): blocco pieno con
 *     l'orologio. Non hanno una durata nel database: nella griglia occupano
 *     un'ora nominale, e il riquadro di dettaglio lo dice.
 *   - SESSIONI DI LAVORO (quando si e' lavorato davvero, dal timer): blocco
 *     chiaro alto quanto le ore fatte. Un timer ancora aperto arriva fino ad
 *     adesso e lampeggia sul bordo.
 * Il colore e' quello dello STATO dell'intervento, lo stesso dell'elenco: i
 * filtri per stato e la legenda sono la stessa cosa.
 *
 * Una sessione a cavallo della mezzanotte si spezza nei due giorni. Le ore
 * contate nel giorno tolgono la pausa in proporzione: il totale di un giorno
 * torna con quello del conto.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, startOfDay,
  addDays, addWeeks, addMonths, addYears, format, isSameDay, isSameMonth, eachDayOfInterval,
} from 'date-fns';
import { it } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Clock, X, ExternalLink, User, MapPin, Printer } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import ResocontoButton from '../components/ResocontoButton';
import RegistroPdfModal from '../components/RegistroPdfModal';
import apiClient from '../api/client';

const VISTE = [
  { id: 'giorno', label: 'Giorno' },
  { id: 'settimana', label: 'Settimana' },
  { id: 'mese', label: 'Mese' },
  { id: 'anno', label: 'Anno' },
];

const STATO_LABEL = {
  in_attesa: 'In attesa', confermato: 'Confermato', in_corso: 'In corso',
  completato: 'Completato', annullato: 'Annullato',
};

const ORA_PX = 46;               // altezza di un'ora nella griglia
const DURATA_APPUNTAMENTO = 60;  // minuti nominali: la durata non e' nel database
const CHIAVE_FILTRI = 'calendario-filtri';

const iso = (d) => format(d, 'yyyy-MM-dd');
const oreTesto = (h) => {
  const min = Math.round(h * 60);
  const hh = Math.floor(min / 60);
  const mm = min % 60;
  return hh ? (mm ? `${hh} h ${mm}` : `${hh} h`) : `${mm} min`;
};

// Il periodo da chiedere al server per la vista: la vista Mese prende le
// settimane intere, perche' la griglia mostra anche i giorni dei mesi accanto.
function periodo(vista, rif) {
  if (vista === 'giorno') return { dal: startOfDay(rif), al: startOfDay(rif) };
  if (vista === 'settimana') return { dal: startOfWeek(rif, { weekStartsOn: 1 }), al: endOfWeek(rif, { weekStartsOn: 1 }) };
  if (vista === 'mese') {
    return {
      dal: startOfWeek(startOfMonth(rif), { weekStartsOn: 1 }),
      al: endOfWeek(endOfMonth(rif), { weekStartsOn: 1 }),
    };
  }
  return { dal: startOfYear(rif), al: endOfYear(rif) };
}

function sposta(vista, rif, n) {
  if (vista === 'giorno') return addDays(rif, n);
  if (vista === 'settimana') return addWeeks(rif, n);
  if (vista === 'mese') return addMonths(rif, n);
  return addYears(rif, n);
}

function titolo(vista, rif) {
  if (vista === 'giorno') return format(rif, 'EEEE d MMMM yyyy', { locale: it });
  if (vista === 'settimana') {
    const a = startOfWeek(rif, { weekStartsOn: 1 });
    const b = endOfWeek(rif, { weekStartsOn: 1 });
    return isSameMonth(a, b)
      ? `${format(a, 'd')} – ${format(b, 'd MMMM yyyy', { locale: it })}`
      : `${format(a, 'd MMM', { locale: it })} – ${format(b, 'd MMM yyyy', { locale: it })}`;
  }
  if (vista === 'mese') return format(rif, 'MMMM yyyy', { locale: it });
  return format(rif, 'yyyy');
}

// Tutti gli eventi, appuntamenti e sessioni, spezzati per giorno.
// Ogni pezzo: { chiave, tipo, giorno, inizio, fine, ore, ev }.
function spezzaPerGiorno(appuntamenti, sessioni) {
  const pezzi = [];
  for (const a of appuntamenti) {
    const inizio = new Date(a.data_appuntamento);
    pezzi.push({
      chiave: `a-${a.id}`, tipo: 'appuntamento', giorno: iso(inizio),
      inizio, fine: new Date(inizio.getTime() + DURATA_APPUNTAMENTO * 60000), ore: 0, ev: a,
    });
  }
  for (const s of sessioni) {
    const inizio = new Date(s.inizio);
    const fine = new Date(s.fine);
    const durataH = Math.max((fine - inizio) / 3600000, 0.0001);
    // Le ore vere (gia' senza pausa) distribuite sui giorni in proporzione.
    const scala = s.in_corso || s.ore === null ? 1 : Math.min(1, s.ore / durataH);
    let g = startOfDay(inizio);
    while (g < fine) {
      const domani = addDays(g, 1);
      const a = inizio > g ? inizio : g;
      const b = fine < domani ? fine : domani;
      if (b > a) {
        pezzi.push({
          chiave: `s-${s.id}-${iso(g)}`, tipo: 'sessione', giorno: iso(g),
          inizio: a, fine: b, ore: ((b - a) / 3600000) * scala, ev: s,
        });
      }
      g = domani;
    }
  }
  return pezzi;
}

// Colonne per i blocchi che si sovrappongono nello stesso giorno, come fa
// Google: ogni gruppo di blocchi accavallati si divide la larghezza.
function disponi(pezzi) {
  const ordinati = [...pezzi].sort((x, y) => x.inizio - y.inizio || y.fine - x.fine);
  const fuori = [];
  let gruppo = [];
  let colonne = [];
  let fineGruppo = 0;
  const chiudi = () => {
    for (const p of gruppo) p.nColonne = colonne.length;
    fuori.push(...gruppo);
    gruppo = [];
    colonne = [];
  };
  for (const p of ordinati) {
    if (gruppo.length && p.inizio >= fineGruppo) chiudi();
    let col = colonne.findIndex((f) => f <= p.inizio);
    if (col === -1) { col = colonne.length; colonne.push(p.fine); } else colonne[col] = p.fine;
    p.colonna = col;
    gruppo.push(p);
    fineGruppo = Math.max(fineGruppo, p.fine.getTime());
  }
  if (gruppo.length) chiudi();
  return fuori;
}

function leggiFiltri() {
  try { return JSON.parse(localStorage.getItem(CHIAVE_FILTRI)) || null; } catch { return null; }
}

export default function Calendario() {
  const salvati = leggiFiltri();
  const [vista, setVista] = useState(() => salvati?.vista || (window.innerWidth < 760 ? 'giorno' : 'settimana'));
  const [rif, setRif] = useState(() => new Date());
  // Gli stati NASCOSTI, non quelli mostrati: uno stato nuovo aggiunto domani al
  // database deve comparire da solo, non restare invisibile finche' qualcuno lo spunta.
  const [statiNascosti, setStatiNascosti] = useState(() => salvati?.statiNascosti || ['annullato']);
  const [mostraApp, setMostraApp] = useState(() => salvati?.mostraApp ?? true);
  const [mostraSes, setMostraSes] = useState(() => salvati?.mostraSes ?? true);
  const [tecnico, setTecnico] = useState(() => salvati?.tecnico || '');
  const [scelto, setScelto] = useState(null); // { pezzo, x, y }
  const [stampa, setStampa] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(CHIAVE_FILTRI, JSON.stringify({ vista, statiNascosti, mostraApp, mostraSes, tecnico }));
    } catch { /* senza localStorage si perde solo la preferenza */ }
  }, [vista, statiNascosti, mostraApp, mostraSes, tecnico]);

  const p = periodo(vista, rif);
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['calendario', iso(p.dal), iso(p.al)],
    queryFn: async () => (await apiClient.get('/calendario', { params: { dal: iso(p.dal), al: iso(p.al) } })).data,
    placeholderData: (prima) => prima,
    // Il timer di un tecnico avanza: ogni minuto si ridisegna con i dati nuovi.
    refetchInterval: 60000,
  });

  const pezzi = useMemo(() => {
    if (!data) return [];
    const okStato = (st) => !statiNascosti.includes(st);
    const app = mostraApp ? data.appuntamenti.filter((a) => okStato(a.stato)
      && (!tecnico || a.tecnici.some((t) => t.id === tecnico))) : [];
    const ses = mostraSes ? data.sessioni.filter((s) => okStato(s.stato) && (!tecnico || s.tecnico_id === tecnico)) : [];
    return spezzaPerGiorno(app, ses);
  }, [data, statiNascosti, mostraApp, mostraSes, tecnico]);

  const perGiorno = useMemo(() => {
    const m = new Map();
    for (const x of pezzi) {
      if (!m.has(x.giorno)) m.set(x.giorno, []);
      m.get(x.giorno).push(x);
    }
    return m;
  }, [pezzi]);

  const totOre = pezzi.reduce((a, x) => a + x.ore, 0);
  const totApp = pezzi.filter((x) => x.tipo === 'appuntamento').length;
  const nInterventi = new Set(pezzi.map((x) => x.ev.intervento_id || x.ev.id)).size;

  const vaiAlGiorno = (d) => { setRif(d); setVista('giorno'); setScelto(null); };
  const apri = (pezzo, e) => {
    e.stopPropagation();
    setScelto({ pezzo, x: e.clientX, y: e.clientY });
  };

  const colore = (st) => data?.stati.find((s) => s.nome === st)?.colore || '#6B7280';

  return (
    <AdminLayout title="Calendario">
      {/* --- barra in alto --- */}
      <div style={s.barra}>
        <div style={s.nav}>
          <button style={s.btnOggi} onClick={() => setRif(new Date())}>Oggi</button>
          <button style={s.freccia} onClick={() => setRif(sposta(vista, rif, -1))} aria-label="Indietro"><ChevronLeft size={18} /></button>
          <button style={s.freccia} onClick={() => setRif(sposta(vista, rif, 1))} aria-label="Avanti"><ChevronRight size={18} /></button>
          <h2 style={s.titolo}>{titolo(vista, rif)}</h2>
          {isFetching && <span style={s.aggiorna}>aggiornamento...</span>}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button style={s.btnStampa} onClick={() => setStampa(true)}><Printer size={16} /> Stampa / PDF</button>
        <div style={s.viste}>
          {VISTE.map((v) => (
            <button key={v.id} onClick={() => setVista(v.id)} style={{ ...s.vistaBtn, ...(vista === v.id ? s.vistaAttiva : {}) }}>{v.label}</button>
          ))}
        </div>
        </div>
      </div>

      {/* --- filtri --- */}
      <div style={s.filtri}>
        <div style={s.gruppoFiltri}>
          {(data?.stati || []).map((st) => {
            const nascosto = statiNascosti.includes(st.nome);
            return (
              <button key={st.nome} style={{ ...s.chip, ...(nascosto ? s.chipSpento : {}) }}
                onClick={() => setStatiNascosti(nascosto ? statiNascosti.filter((x) => x !== st.nome) : [...statiNascosti, st.nome])}
                title={nascosto ? 'Mostra' : 'Nascondi'}>
                <span style={{ ...s.pallino, backgroundColor: nascosto ? 'transparent' : st.colore, borderColor: st.colore }} />
                {STATO_LABEL[st.nome] || st.nome}
              </button>
            );
          })}
        </div>
        <div style={s.gruppoFiltri}>
          <label style={s.spunta}><input type="checkbox" checked={mostraApp} onChange={(e) => setMostraApp(e.target.checked)} /> Appuntamenti</label>
          <label style={s.spunta}><input type="checkbox" checked={mostraSes} onChange={(e) => setMostraSes(e.target.checked)} /> Ore lavorate</label>
          <select style={s.select} value={tecnico} onChange={(e) => setTecnico(e.target.value)}>
            <option value="">Tutti i tecnici</option>
            {(data?.tecnici || []).map((t) => <option key={t.id} value={t.id}>{t.nome}{t.tecnico_esterno ? ' (esterno)' : ''}</option>)}
          </select>
        </div>
      </div>

      <div style={s.riassunto}>
        <span><strong>{oreTesto(totOre)}</strong> lavorate</span>
        <span><strong>{totApp}</strong> appuntament{totApp === 1 ? 'o' : 'i'}</span>
        <span><strong>{nInterventi}</strong> interventi</span>
      </div>

      {error ? (
        <div style={s.vuoto}>{error.response?.data?.error || 'Errore nel caricamento del calendario'}</div>
      ) : isLoading && !data ? (
        <div style={s.vuoto}>Caricamento...</div>
      ) : (
        <div style={s.foglio} onClick={() => setScelto(null)}>
          {(vista === 'giorno' || vista === 'settimana') && (
            <GrigliaOraria
              giorni={eachDayOfInterval({ start: p.dal, end: p.al })}
              perGiorno={perGiorno} colore={colore} onApri={apri} onGiorno={vaiAlGiorno}
            />
          )}
          {vista === 'mese' && (
            <VistaMese rif={rif} giorni={eachDayOfInterval({ start: p.dal, end: p.al })}
              perGiorno={perGiorno} colore={colore} onApri={apri} onGiorno={vaiAlGiorno} />
          )}
          {vista === 'anno' && <VistaAnno rif={rif} perGiorno={perGiorno} onGiorno={vaiAlGiorno} />}
        </div>
      )}

      {scelto && <Dettaglio scelto={scelto} colore={colore} onChiudi={() => setScelto(null)} />}
      {stampa && (
        // Nel mese la griglia mostra anche i giorni dei mesi accanto: la stampa
        // parte dal mese vero, non dalle settimane intere.
        <RegistroPdfModal
          dal={iso(vista === 'mese' ? startOfMonth(rif) : p.dal)}
          al={iso(vista === 'mese' ? endOfMonth(rif) : p.al)}
          tecnico={tecnico}
          nomeTecnico={data?.tecnici.find((t) => t.id === tecnico)?.nome || ''}
          nascosti={statiNascosti}
          onChiudi={() => setStampa(false)}
        />
      )}
    </AdminLayout>
  );
}

// ============================================================
// Giorno e settimana: la griglia oraria
// ============================================================
function GrigliaOraria({ giorni, perGiorno, colore, onApri, onGiorno }) {
  const scorri = useRef(null);
  const [adesso, setAdesso] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setAdesso(new Date()), 60000);
    return () => clearInterval(t);
  }, []);
  // Si apre sulle 7 del mattino: la notte e' vuota quasi sempre.
  useEffect(() => { if (scorri.current) scorri.current.scrollTop = 7 * ORA_PX - 8; }, []);

  const colonna = { flex: 1, minWidth: giorni.length > 1 ? '96px' : '200px' };

  // Intestazione e corpo nello STESSO contenitore che scorre: separati, su uno
  // schermo stretto le colonne dei giorni si disallineerebbero dalle loro ore.
  return (
    <div ref={scorri} style={{ maxHeight: '70vh', overflow: 'auto' }}>
      <div style={{ minWidth: giorni.length > 1 ? `${52 + giorni.length * 96}px` : undefined }}>
      {/* intestazione dei giorni, con le ore del giorno: resta in vista scorrendo */}
      <div style={{ display: 'flex', borderBottom: '1px solid #E5E7EB', position: 'sticky', top: 0, zIndex: 5, backgroundColor: 'white' }}>
        <div style={{ width: '52px', flexShrink: 0 }} />
        {giorni.map((g) => {
          const lista = perGiorno.get(iso(g)) || [];
          const ore = lista.reduce((a, x) => a + x.ore, 0);
          const oggi = isSameDay(g, adesso);
          return (
            <button key={iso(g)} style={{ ...colonna, ...s.testaGiorno }} onClick={() => onGiorno(g)}>
              <span style={s.testaNome}>{format(g, 'EEE', { locale: it })}</span>
              <span style={{ ...s.testaNumero, ...(oggi ? s.testaOggi : {}) }}>{format(g, 'd')}</span>
              <span style={s.testaOre}>{ore > 0 ? oreTesto(ore) : ' '}</span>
            </button>
          );
        })}
      </div>

        <div style={{ display: 'flex', position: 'relative', height: 24 * ORA_PX }}>
          {/* colonna delle ore */}
          <div style={{ width: '52px', flexShrink: 0, position: 'relative' }}>
            {Array.from({ length: 24 }, (_, h) => (
              <div key={h} style={{ ...s.etichettaOra, top: h * ORA_PX - 6 }}>{h === 0 ? '' : `${String(h).padStart(2, '0')}:00`}</div>
            ))}
          </div>
          {giorni.map((g) => {
            const inizioGiorno = startOfDay(g);
            const blocchi = disponi((perGiorno.get(iso(g)) || []).map((x) => ({ ...x })));
            const oggi = isSameDay(g, adesso);
            return (
              <div key={iso(g)} style={{ ...colonna, position: 'relative', borderLeft: '1px solid #F3F4F6', backgroundColor: oggi ? '#FAF5FF' : 'white' }}>
                {Array.from({ length: 24 }, (_, h) => (
                  <div key={h} style={{ position: 'absolute', left: 0, right: 0, top: h * ORA_PX, borderTop: '1px solid #F3F4F6' }} />
                ))}
                {blocchi.map((b) => {
                  const top = ((b.inizio - inizioGiorno) / 60000) * (ORA_PX / 60);
                  const alt = Math.max(((b.fine - b.inizio) / 60000) * (ORA_PX / 60), 18);
                  const larg = 100 / b.nColonne;
                  const c = colore(b.ev.stato);
                  const app = b.tipo === 'appuntamento';
                  return (
                    <button
                      key={b.chiave}
                      onClick={(e) => onApri(b, e)}
                      style={{
                        ...s.blocco,
                        top, height: alt - 2,
                        left: `calc(${b.colonna * larg}% + 2px)`, width: `calc(${larg}% - 4px)`,
                        backgroundColor: app ? c : `${c}22`,
                        color: app ? 'white' : '#111827',
                        borderLeft: `3px solid ${c}`,
                        ...(b.ev.in_corso ? s.bloccoInCorso : {}),
                        opacity: b.ev.di_prova ? 0.6 : 1,
                      }}
                      title={`${b.ev.codice} — ${b.ev.titolo}`}
                    >
                      <span style={s.bloccoRiga1}>
                        {app && <Clock size={11} style={{ marginRight: 3, verticalAlign: '-1px' }} />}
                        {format(b.inizio, 'HH:mm')}{!app && ` – ${b.ev.in_corso && isSameDay(b.fine, adesso) ? 'ora' : format(b.fine, 'HH:mm')}`}
                        {' '}<strong>{b.ev.codice}</strong>
                      </span>
                      {alt > 34 && <span style={s.bloccoRiga2}>{b.ev.titolo}</span>}
                      {alt > 52 && <span style={s.bloccoRiga3}>{app ? (b.ev.cliente || '') : `${b.ev.tecnico} · ${oreTesto(b.ore)}`}</span>}
                    </button>
                  );
                })}
                {oggi && (
                  <div style={{ ...s.lineaAdesso, top: ((adesso - inizioGiorno) / 60000) * (ORA_PX / 60) }}>
                    <span style={s.pallinoAdesso} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Mese
// ============================================================
function VistaMese({ rif, giorni, perGiorno, colore, onApri, onGiorno }) {
  const oggi = new Date();
  const MAX = 4;
  return (
    <div>
      <div style={s.meseTesta}>
        {['lun', 'mar', 'mer', 'gio', 'ven', 'sab', 'dom'].map((g) => <div key={g} style={s.meseTestaCella}>{g}</div>)}
      </div>
      <div style={s.meseGriglia}>
        {giorni.map((g) => {
          const lista = perGiorno.get(iso(g)) || [];
          // Le sessioni dello stesso intervento nello stesso giorno diventano una
          // riga sola con le ore sommate: nel mese conta "quanto", non "quando".
          const app = lista.filter((x) => x.tipo === 'appuntamento').sort((a, b) => a.inizio - b.inizio);
          const perInt = new Map();
          for (const x of lista.filter((y) => y.tipo === 'sessione')) {
            const k = x.ev.intervento_id;
            if (!perInt.has(k)) perInt.set(k, { ...x, ore: 0, chiave: `m-${k}-${iso(g)}` });
            perInt.get(k).ore += x.ore;
          }
          const righe = [...app, ...perInt.values()];
          const ore = lista.reduce((a, x) => a + x.ore, 0);
          const fuoriMese = !isSameMonth(g, rif);
          return (
            <div key={iso(g)} style={{ ...s.meseCella, ...(fuoriMese ? s.meseFuori : {}) }}>
              <div style={s.meseCellaTesta}>
                <button style={{ ...s.meseNumero, ...(isSameDay(g, oggi) ? s.testaOggi : {}) }} onClick={(e) => { e.stopPropagation(); onGiorno(g); }}>
                  {format(g, 'd')}
                </button>
                {ore > 0 && <span style={s.meseOre}>{oreTesto(ore)}</span>}
              </div>
              {righe.slice(0, MAX).map((x) => {
                const c = colore(x.ev.stato);
                const app = x.tipo === 'appuntamento';
                return (
                  // Una riga di ore sommate non e' una sessione sola: porta al giorno.
                  <button key={x.chiave} onClick={(e) => { if (app) onApri(x, e); else { e.stopPropagation(); onGiorno(g); } }}
                    style={{ ...s.meseRiga, ...(app ? { backgroundColor: c, color: 'white' } : { borderLeft: `3px solid ${c}`, backgroundColor: `${c}18` }), opacity: x.ev.di_prova ? 0.6 : 1 }}
                    title={`${x.ev.codice} — ${x.ev.titolo}`}>
                    {app ? <>{format(x.inizio, 'HH:mm')} {x.ev.codice}</> : <>{x.ev.codice} · {oreTesto(x.ore)}</>}
                  </button>
                );
              })}
              {righe.length > MAX && (
                <button style={s.meseAltri} onClick={(e) => { e.stopPropagation(); onGiorno(g); }}>+{righe.length - MAX} altri</button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// Anno: dodici mesi piccoli, ogni giorno colorato dalle ore lavorate
// ============================================================
function VistaAnno({ rif, perGiorno, onGiorno }) {
  const oggi = new Date();
  const max = Math.max(1, ...[...perGiorno.values()].map((l) => l.reduce((a, x) => a + x.ore, 0)));
  const mesi = Array.from({ length: 12 }, (_, m) => new Date(rif.getFullYear(), m, 1));
  return (
    <div>
      <div style={s.annoGriglia}>
        {mesi.map((m) => {
          const giorni = eachDayOfInterval({ start: startOfWeek(m, { weekStartsOn: 1 }), end: endOfWeek(endOfMonth(m), { weekStartsOn: 1 }) });
          let oreMese = 0;
          let appMese = 0;
          for (const g of eachDayOfInterval({ start: m, end: endOfMonth(m) })) {
            for (const x of perGiorno.get(iso(g)) || []) { oreMese += x.ore; if (x.tipo === 'appuntamento') appMese++; }
          }
          return (
            <div key={m.getMonth()} style={s.annoMese}>
              <div style={s.annoMeseTesta}>
                <strong style={{ textTransform: 'capitalize' }}>{format(m, 'MMMM', { locale: it })}</strong>
                <span style={s.annoMeseDati}>{oreMese > 0 ? oreTesto(oreMese) : '—'}{appMese ? ` · ${appMese} app.` : ''}</span>
              </div>
              <div style={s.annoGiorni}>
                {['L', 'M', 'M', 'G', 'V', 'S', 'D'].map((g, k) => <span key={k} style={s.annoTestaGiorno}>{g}</span>)}
                {giorni.map((g) => {
                  if (!isSameMonth(g, m)) return <span key={iso(g)} />;
                  const lista = perGiorno.get(iso(g)) || [];
                  const ore = lista.reduce((a, x) => a + x.ore, 0);
                  const haApp = lista.some((x) => x.tipo === 'appuntamento');
                  const intensita = ore > 0 ? 0.18 + 0.82 * Math.min(1, ore / max) : 0;
                  return (
                    <button key={iso(g)} onClick={(e) => { e.stopPropagation(); onGiorno(g); }}
                      title={`${format(g, 'd MMMM', { locale: it })}: ${ore > 0 ? oreTesto(ore) : 'nessuna ora'}${haApp ? ', con appuntamenti' : ''}`}
                      style={{
                        ...s.annoGiorno,
                        backgroundColor: intensita ? `rgba(139, 92, 246, ${intensita})` : '#F9FAFB',
                        color: intensita > 0.55 ? 'white' : '#374151',
                        ...(isSameDay(g, oggi) ? { outline: '2px solid #111827', outlineOffset: '-2px' } : {}),
                      }}>
                      {format(g, 'd')}
                      {haApp && <span style={s.annoPunto} />}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <div style={s.legendaAnno}>
        Meno ore
        {[0.18, 0.4, 0.6, 0.8, 1].map((a) => <span key={a} style={{ ...s.legendaQuadro, backgroundColor: `rgba(139, 92, 246, ${a})` }} />)}
        Più ore · <span style={{ ...s.annoPunto, position: 'static', display: 'inline-block' }} /> appuntamento
      </div>
    </div>
  );
}

// ============================================================
// Il riquadro di dettaglio al clic
// ============================================================
function Dettaglio({ scelto, colore, onChiudi }) {
  const navigate = useNavigate();
  const { pezzo } = scelto;
  const ev = pezzo.ev;
  const app = pezzo.tipo === 'appuntamento';
  const idIntervento = app ? ev.id : ev.intervento_id;
  const larg = 320;
  const x = Math.min(Math.max(scelto.x - larg / 2, 12), window.innerWidth - larg - 12);
  const y = Math.min(scelto.y + 12, window.innerHeight - 300);
  const c = colore(ev.stato);

  return (
    <div style={{ ...s.dettaglio, left: x, top: Math.max(12, y), width: larg }} onClick={(e) => e.stopPropagation()}>
      <div style={{ ...s.dettaglioBarra, backgroundColor: c }} />
      <div style={s.dettaglioCorpo}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
          <span style={s.dettaglioTipo}>{app ? 'Appuntamento' : (ev.in_corso ? 'Lavoro in corso' : 'Sessione di lavoro')}</span>
          <button style={s.chiudi} onClick={onChiudi} aria-label="Chiudi"><X size={16} /></button>
        </div>
        <div style={s.dettaglioTitolo}>{ev.titolo}</div>
        <div style={s.dettaglioRiga}>
          <code style={s.codice}>{ev.codice}</code>
          <span style={{ ...s.statoBadge, backgroundColor: `${c}22`, color: c }}>{STATO_LABEL[ev.stato] || ev.stato}</span>
          {ev.di_prova && <span style={s.prova}>prova</span>}
        </div>
        <div style={s.dettaglioInfo}>
          <Clock size={14} />
          {app
            ? <span>{format(pezzo.inizio, "EEEE d MMMM, 'ore' HH:mm", { locale: it })}<span style={s.tenue}> · durata non indicata</span></span>
            : <span>{format(new Date(ev.inizio), 'd MMM HH:mm', { locale: it })} – {ev.in_corso ? 'in corso' : format(new Date(ev.fine), isSameDay(new Date(ev.inizio), new Date(ev.fine)) ? 'HH:mm' : 'd MMM HH:mm', { locale: it })}
              {ev.ore !== null && !ev.in_corso && <strong> · {oreTesto(ev.ore)}</strong>}
              {ev.pausa_minuti > 0 && <span style={s.tenue}> (pausa {ev.pausa_minuti} min)</span>}
            </span>}
        </div>
        {ev.cliente && <div style={s.dettaglioInfo}><User size={14} /> {ev.cliente}</div>}
        {app && (ev.indirizzo || ev.citta) && <div style={s.dettaglioInfo}><MapPin size={14} /> {[ev.indirizzo, ev.citta].filter(Boolean).join(', ')}</div>}
        <div style={s.dettaglioInfo}>
          <span style={s.tenue}>{app ? 'Tecnici:' : 'Tecnico:'}</span>
          {app ? (ev.tecnici.length ? ev.tecnici.map((t) => t.nome).join(', ') : 'nessuno assegnato') : `${ev.tecnico}${ev.tecnico_esterno ? ' (esterno)' : ''}`}
        </div>
        <div style={s.dettaglioAzioni}>
          <button style={s.btnApri} onClick={() => navigate(`/interventi/${idIntervento}`)}><ExternalLink size={14} /> Apri intervento</button>
          <ResocontoButton interventoId={idIntervento} codice={ev.codice} compatto />
        </div>
      </div>
    </div>
  );
}

const s = {
  barra: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' },
  nav: { display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' },
  btnOggi: { padding: '0.45rem 0.9rem', border: '1px solid #D1D5DB', backgroundColor: 'white', borderRadius: '8px', fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer', minHeight: '36px' },
  freccia: { width: '36px', height: '36px', border: 'none', background: 'none', borderRadius: '50%', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#374151' },
  titolo: { margin: '0 0 0 0.35rem', fontSize: '1.3rem', fontWeight: 600, textTransform: 'capitalize', color: '#111827' },
  aggiorna: { fontSize: '0.75rem', color: '#9CA3AF' },
  viste: { display: 'inline-flex', border: '1px solid #D1D5DB', borderRadius: '8px', overflow: 'hidden' },
  btnStampa: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.45rem 0.85rem', border: '1px solid #D1D5DB', backgroundColor: 'white', borderRadius: '8px', fontWeight: 600, fontSize: '0.86rem', cursor: 'pointer', minHeight: '36px', color: '#374151' },
  vistaBtn: { padding: '0.45rem 0.85rem', border: 'none', borderRight: '1px solid #E5E7EB', backgroundColor: 'white', fontSize: '0.86rem', fontWeight: 600, color: '#4B5563', cursor: 'pointer', minHeight: '36px' },
  vistaAttiva: { backgroundColor: '#EDE9FE', color: '#6D28D9' },
  filtri: { display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.6rem' },
  gruppoFiltri: { display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' },
  chip: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.3rem 0.7rem', border: '1px solid #E5E7EB', backgroundColor: 'white', borderRadius: '999px', fontSize: '0.8rem', fontWeight: 600, color: '#374151', cursor: 'pointer', minHeight: '32px' },
  chipSpento: { color: '#9CA3AF', backgroundColor: '#F9FAFB', textDecoration: 'line-through' },
  pallino: { width: '10px', height: '10px', borderRadius: '50%', border: '2px solid', boxSizing: 'border-box' },
  spunta: { display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.84rem', color: '#374151', cursor: 'pointer', marginRight: '0.4rem' },
  select: { padding: '0.4rem 0.6rem', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '0.84rem', backgroundColor: 'white', minHeight: '34px' },
  riassunto: { display: 'flex', gap: '1.25rem', flexWrap: 'wrap', fontSize: '0.84rem', color: '#4B5563', marginBottom: '0.6rem' },
  vuoto: { padding: '3rem', textAlign: 'center', color: '#6B7280', backgroundColor: 'white', borderRadius: '12px' },
  foglio: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' },

  testaGiorno: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0.5rem 0.25rem 0.4rem', border: 'none', borderLeft: '1px solid #F3F4F6', background: 'white', cursor: 'pointer', fontFamily: 'inherit' },
  testaNome: { fontSize: '0.72rem', textTransform: 'uppercase', color: '#6B7280', fontWeight: 600, letterSpacing: '0.04em' },
  testaNumero: { fontSize: '1.35rem', fontWeight: 500, color: '#111827', width: '40px', height: '40px', lineHeight: '40px', borderRadius: '50%', textAlign: 'center' },
  testaOggi: { backgroundColor: '#8B5CF6', color: 'white' },
  testaOre: { fontSize: '0.72rem', color: '#7C3AED', fontWeight: 600, minHeight: '1em' },
  etichettaOra: { position: 'absolute', right: '6px', fontSize: '0.68rem', color: '#9CA3AF' },
  blocco: { position: 'absolute', overflow: 'hidden', borderRadius: '6px', border: 'none', padding: '2px 5px', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', boxShadow: '0 1px 2px rgba(0,0,0,0.06)' },
  bloccoInCorso: { backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.0) 0 6px, rgba(255,255,255,0.55) 6px 12px)' },
  bloccoRiga1: { fontSize: '0.7rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  bloccoRiga2: { fontSize: '0.72rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 600 },
  bloccoRiga3: { fontSize: '0.68rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', opacity: 0.85 },
  lineaAdesso: { position: 'absolute', left: 0, right: 0, height: 0, borderTop: '2px solid #EA4335', zIndex: 3, pointerEvents: 'none' },
  pallinoAdesso: { position: 'absolute', left: '-5px', top: '-6px', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#EA4335' },

  meseTesta: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid #E5E7EB' },
  meseTestaCella: { padding: '0.45rem', textAlign: 'center', fontSize: '0.72rem', textTransform: 'uppercase', color: '#6B7280', fontWeight: 600 },
  meseGriglia: { display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' },
  meseCella: { minHeight: '112px', borderRight: '1px solid #F3F4F6', borderBottom: '1px solid #F3F4F6', padding: '0.25rem', display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 },
  meseFuori: { backgroundColor: '#FAFAFA', opacity: 0.6 },
  meseCellaTesta: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' },
  meseNumero: { width: '26px', height: '26px', borderRadius: '50%', border: 'none', background: 'none', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', color: '#111827', padding: 0 },
  meseOre: { fontSize: '0.68rem', color: '#7C3AED', fontWeight: 700 },
  meseRiga: { display: 'block', width: '100%', textAlign: 'left', border: 'none', borderRadius: '4px', padding: '1px 4px', fontSize: '0.7rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer', fontFamily: 'inherit', color: '#111827' },
  meseAltri: { border: 'none', background: 'none', textAlign: 'left', fontSize: '0.7rem', color: '#6B7280', fontWeight: 600, cursor: 'pointer', padding: '1px 4px' },

  annoGriglia: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '1.25rem', padding: '1rem' },
  annoMese: {},
  annoMeseTesta: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.4rem', fontSize: '0.92rem' },
  annoMeseDati: { fontSize: '0.72rem', color: '#7C3AED', fontWeight: 600 },
  annoGiorni: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px' },
  annoTestaGiorno: { fontSize: '0.62rem', textAlign: 'center', color: '#9CA3AF', fontWeight: 600 },
  annoGiorno: { position: 'relative', aspectRatio: '1', border: 'none', borderRadius: '5px', fontSize: '0.68rem', cursor: 'pointer', padding: 0, fontFamily: 'inherit' },
  annoPunto: { position: 'absolute', bottom: '2px', left: '50%', marginLeft: '-2px', width: '4px', height: '4px', borderRadius: '50%', backgroundColor: '#F59E0B' },
  legendaAnno: { display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.75rem', color: '#6B7280', padding: '0 1rem 1rem', flexWrap: 'wrap' },
  legendaQuadro: { width: '14px', height: '14px', borderRadius: '3px', display: 'inline-block' },

  dettaglio: { position: 'fixed', zIndex: 1000, backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 12px 32px rgba(0,0,0,0.18)', overflow: 'visible' },
  dettaglioBarra: { height: '6px', borderRadius: '12px 12px 0 0' },
  dettaglioCorpo: { padding: '0.8rem 1rem 1rem' },
  dettaglioTipo: { fontSize: '0.72rem', textTransform: 'uppercase', color: '#6B7280', fontWeight: 700, letterSpacing: '0.04em' },
  chiudi: { border: 'none', background: 'none', cursor: 'pointer', color: '#6B7280', padding: 0, display: 'inline-flex' },
  dettaglioTitolo: { fontSize: '1.02rem', fontWeight: 700, color: '#111827', margin: '0.25rem 0 0.4rem', lineHeight: 1.3 },
  dettaglioRiga: { display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.5rem' },
  codice: { fontSize: '0.78rem', backgroundColor: '#F3F4F6', padding: '0.1rem 0.4rem', borderRadius: '4px' },
  statoBadge: { fontSize: '0.74rem', fontWeight: 700, padding: '0.1rem 0.5rem', borderRadius: '5px' },
  prova: { fontSize: '0.7rem', color: '#92400E', backgroundColor: '#FEF3C7', padding: '0.05rem 0.35rem', borderRadius: '4px' },
  dettaglioInfo: { display: 'flex', alignItems: 'flex-start', gap: '0.45rem', fontSize: '0.85rem', color: '#374151', marginTop: '0.35rem', lineHeight: 1.4 },
  tenue: { color: '#9CA3AF' },
  dettaglioAzioni: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.85rem' },
  btnApri: { display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.4rem 0.75rem', backgroundColor: '#8B5CF6', color: 'white', border: 'none', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' },
};
